import { NavigationProp, useNavigation } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';
import { useBreakpointValueGS } from 'hooks/useBreakpointValueGS';
import { RootStackParamList } from 'navigation/AppNavigator';
import React, { useEffect, useRef } from 'react';
import {
  AccessibilityInfo,
  Animated,
  Easing,
  Image,
  ImageSourcePropType,
  Linking,
  Pressable,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
} from 'react-native';
import { useGetHelpQuery } from 'services/cms';
import theme from 'theme';

import DevicesImage from 'assets/footer-devices-mockup.png';
import TizenLogo from 'assets/footer-tizen.png';

import Android from 'components/Icons/footer/Android';
import AndroidTv from 'components/Icons/footer/AndroidTv';
import Chromecast from 'components/Icons/footer/Chromecast';
import Facebook from 'components/Icons/footer/Facebook';
import FireTv from 'components/Icons/footer/FireTv';
import GoogleTv from 'components/Icons/footer/GoogleTv';
import Instagram from 'components/Icons/footer/Instagram';
import Ios from 'components/Icons/footer/Ios';
import Web from 'components/Icons/footer/Web';
import WebOs from 'components/Icons/footer/WebOs';
import XTwitter from 'components/Icons/footer/XTwitter';

// O frame do Figma (627:86) é um slide de 1920 em que o header do app, que tem 80 de altura,
// aparece com 111. As medidas deste arquivo são as do Figma e passam por aqui para voltar à
// escala do app.
const FIGMA_SCALE = 80 / 111;

const fromFigma = (value: number) => Math.round(value * FIGMA_SCALE);

type PlatformLogo = {
  name: string;
  width: number;
  height: number;
  offsetY?: number;
  Logo?: React.FC<any>;
  image?: ImageSourcePropType;
};

const PLATFORMS: PlatformLogo[] = [
  { name: 'Android', Logo: Android, width: 138.5, height: 22 },
  { name: 'Android TV', Logo: AndroidTv, width: 127.2, height: 22 },
  { name: 'iOS', Logo: Ios, width: 69.5, height: 31.2, offsetY: -3 },
  { name: 'Tizen', image: TizenLogo, width: 90, height: 33.5, offsetY: -2 },
  { name: 'webOS', Logo: WebOs, width: 140, height: 33.5 },
  { name: 'Google TV', Logo: GoogleTv, width: 176.6, height: 31.3 },
  { name: 'Chromecast', Logo: Chromecast, width: 206.4, height: 33.5 },
  { name: 'Fire TV', Logo: FireTv, width: 77.4, height: 41.6, offsetY: 8 },
  { name: 'Web', Logo: Web, width: 102.2, height: 31.7 },
];

const PLATFORMS_LABEL = PLATFORMS.map(({ name }) => name).join(', ');

const PLATFORM_GAP = fromFigma(100);

const PLATFORMS_WIDTH = PLATFORMS.reduce(
  (total, { width }) => total + fromFigma(width) + PLATFORM_GAP,
  0
);

const PLATFORMS_LOOP_DURATION = 15000;

type FooterLink = { title: string; route: keyof RootStackParamList };

const LINKS: FooterLink[] = [
  { title: 'Fale Conosco', route: 'ContactUs' },
  { title: 'Perguntas Frequentes', route: 'FaqSection' },
  { title: 'Termos e Condições', route: 'LegalTerms' },
];

type SocialIcon = { Icon: React.FC<any>; width: number; height: number };

const X_ICON: SocialIcon = { Icon: XTwitter, width: 27, height: 28 };

const SOCIAL_ICONS: Record<string, SocialIcon> = {
  instagram: { Icon: Instagram, width: 31, height: 30 },
  x: X_ICON,
  twitter: X_ICON,
  facebook: { Icon: Facebook, width: 31, height: 30 },
};

const SOCIAL_ORDER = ['instagram', 'x', 'twitter', 'facebook'];

const DARK_BACKGROUND = '#000319';
const PLATFORMS_BACKGROUND = 'rgba(34, 64, 255, 0.29)';
const HERO_GRADIENT = ['rgba(8, 15, 65, 0)', '#00041D'];
const HERO_GRADIENT_LOCATIONS = [0, 0.968];

const HomeFooterDesktop: React.FC = () => {
  const navigation = useNavigation<NavigationProp<RootStackParamList>>();
  const { data } = useGetHelpQuery();
  const { width } = useWindowDimensions();

  const horizontalPadding = useBreakpointValueGS({ base: 24, xl: fromFigma(161) });

  const translateX = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    let animation: Animated.CompositeAnimation | undefined;
    let unmounted = false;

    AccessibilityInfo.isReduceMotionEnabled().then((reduceMotion) => {
      if (unmounted || reduceMotion) return;

      animation = Animated.loop(
        Animated.timing(translateX, {
          toValue: -PLATFORMS_WIDTH,
          duration: PLATFORMS_LOOP_DURATION,
          easing: Easing.linear,
          useNativeDriver: false,
        })
      );
      animation.start();
    });

    return () => {
      unmounted = true;
      animation?.stop();
    };
  }, [translateX]);

  // Uma cópia a mais do que cabe na tela, para a faixa nunca mostrar o fim enquanto desliza.
  const platformCopies = Math.ceil(width / PLATFORMS_WIDTH) + 1;

  const socialMedia = (data?.socialMidia?.midiaItems ?? [])
    .map((midia: any) => {
      const name = String(midia?.socialMidiaName || '')
        .trim()
        .toLowerCase();

      return { name, icon: SOCIAL_ICONS[name], link: midia?.SocialMidiaLink };
    })
    .filter((item: any) => item.icon && item.link)
    .sort((a: any, b: any) => SOCIAL_ORDER.indexOf(a.name) - SOCIAL_ORDER.indexOf(b.name));

  const openLink = (link: string) => {
    Linking.openURL(link).catch((error) => console.error(error));
  };

  return (
    <View style={styles.container}>
      <LinearGradient
        colors={HERO_GRADIENT}
        locations={HERO_GRADIENT_LOCATIONS}
        start={{ x: 0, y: 0 }}
        end={{ x: 0, y: 1 }}
        style={styles.hero}>
        <Image source={DevicesImage} style={styles.devices} />
      </LinearGradient>

      <View style={styles.platforms} role="img" aria-label={PLATFORMS_LABEL}>
        <Animated.View style={[styles.platformsTrack, { transform: [{ translateX }] }]}>
          {Array.from({ length: platformCopies }, (_, copy) => (
            <View key={copy} style={styles.platformsTrack}>
              {PLATFORMS.map(({ name, Logo, image, width: logoWidth, height, offsetY = 0 }) => (
                <View key={name} style={[styles.platform, { top: fromFigma(offsetY) }]}>
                  {Logo ? (
                    <Logo width={fromFigma(logoWidth)} height={fromFigma(height)} />
                  ) : (
                    <Image
                      source={image!}
                      style={{
                        width: fromFigma(logoWidth),
                        height: fromFigma(height),
                        resizeMode: 'contain',
                      }}
                    />
                  )}
                </View>
              ))}
            </View>
          ))}
        </Animated.View>
      </View>

      <View style={[styles.bottom, { paddingHorizontal: horizontalPadding }]}>
        {socialMedia.length > 0 && (
          <View style={styles.social}>
            <Text style={styles.socialTitle}>Siga a gente nas redes sociais</Text>

            {socialMedia.map(({ name, icon, link }: any) => (
              <Pressable
                key={name}
                role="button"
                aria-label={name}
                style={styles.socialButton}
                onPress={() => openLink(link)}>
                <icon.Icon width={fromFigma(icon.width)} height={fromFigma(icon.height)} />
              </Pressable>
            ))}
          </View>
        )}

        <View style={styles.links}>
          {LINKS.map(({ title, route }) => (
            <Pressable
              key={route}
              role="button"
              style={styles.link}
              onPress={() => navigation.navigate<any>(route)}>
              <Text style={styles.linkText}>{title}</Text>
            </Pressable>
          ))}
        </View>
      </View>

      <View style={[styles.copyrightBar, { paddingHorizontal: horizontalPadding }]}>
        <Text style={styles.copyright}>© Bandplay Copyright 2020-{new Date().getFullYear()}</Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
  hero: {
    width: '100%',
    alignItems: 'center',
    paddingTop: '5.57%',
  },
  devices: {
    width: '75%',
    maxWidth: 1440,
    aspectRatio: 1440 / 503,
    resizeMode: 'contain',
  },
  platforms: {
    width: '100%',
    height: fromFigma(124),
    justifyContent: 'center',
    overflow: 'hidden',
    backgroundColor: PLATFORMS_BACKGROUND,
  },
  platformsTrack: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  platform: {
    marginRight: PLATFORM_GAP,
  },
  bottom: {
    width: '100%',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    columnGap: fromFigma(24),
    rowGap: fromFigma(24),
    paddingVertical: fromFigma(55),
  },
  social: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: fromFigma(14),
  },
  socialTitle: {
    marginRight: fromFigma(8),
    color: theme.colors.base.white,
    fontFamily: theme.fonts.inter.bold,
    fontSize: fromFigma(24),
    lineHeight: fromFigma(31),
  },
  socialButton: {
    width: fromFigma(50),
    height: fromFigma(50),
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: theme.radii.full,
    backgroundColor: theme.colors.secondary['500'],
  },
  links: {
    flexDirection: 'row',
    gap: fromFigma(20),
    marginLeft: 'auto',
  },
  link: {
    height: fromFigma(50),
    justifyContent: 'center',
    paddingHorizontal: fromFigma(18),
    borderRadius: fromFigma(10),
    backgroundColor: DARK_BACKGROUND,
  },
  linkText: {
    color: theme.colors.base.white,
    fontFamily: theme.fonts.inter.bold,
    fontSize: fromFigma(22),
    lineHeight: fromFigma(29),
  },
  copyrightBar: {
    width: '100%',
    height: fromFigma(75),
    alignItems: 'flex-end',
    justifyContent: 'center',
    backgroundColor: DARK_BACKGROUND,
  },
  copyright: {
    color: theme.colors.base.white,
    fontFamily: theme.fonts.inter.regular,
    fontSize: fromFigma(22),
    lineHeight: fromFigma(29),
  },
});

export default HomeFooterDesktop;
